import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Truck, Package, MapPin, Phone, Loader2, LogOut, CheckCircle2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import NotificationBell from "../../components/NotificationBell";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { useAuth } from "../../App";
import apiClient from "../../lib/api";
import { toastApiError } from "../../lib/toastApiError";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

const DeliveryDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("nav");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("awaiting_pickup");
  const [acting, setActing] = useState(null);

  const STATUS = {
    awaiting_pickup: { label: t("delivery.statusAwaitingPickup"), className: "bg-amber-100 text-amber-800" },
    assigned: { label: t("delivery.statusAssigned"), className: "bg-blue-100 text-blue-800" },
    picked_up: { label: t("delivery.statusPickedUp"), className: "bg-indigo-100 text-indigo-800" },
    delivered: { label: t("delivery.statusDelivered"), className: "bg-green-100 text-green-800" },
  };

  const load = async () => {
    try {
      const params = {};
      if (filter === "assigned") {
        params.mine = true;
      } else if (filter === "picked_up" || filter === "delivered") {
        params.mine = true;
        params.status = filter;
      } else if (filter !== "all") {
        params.status = filter;
      }
      const res = await apiClient.get("/delivery/jobs", { params });
      setJobs(res.data || []);
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setLoading(true); load(); }, [filter]);

  const act = async (jobId, action) => {
    setActing(jobId);
    try {
      const res = await apiClient.put(`/delivery/jobs/${jobId}/${action}`, { note: null });
      toast.success(res.data.message || t("delivery.updateSuccess"));
      load();
    } catch (error) {
      toastApiError(error);
    } finally {
      setActing(null);
    }
  };

  const filteredJobs = filter === "assigned"
    ? jobs.filter((j) => j.status === "assigned" || j.status === "picked_up")
    : jobs;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="OMU" className="h-10 w-auto" />
            <span className="font-heading font-semibold hidden sm:inline">{t("delivery.title")}</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact />
            <span className="text-sm text-slate-600 hidden sm:inline">{user?.name}</span>
            <NotificationBell />
            <Button variant="outline" onClick={() => { logout(); navigate("/login"); }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2 mb-2">
          <Truck className="h-6 w-6 text-omu-blue-500" />
          {t("delivery.pageTitle")}
        </h1>
        <p className="text-slate-500 mb-6">
          {t("delivery.pageSubtitle")}
        </p>

        <Tabs value={filter} onValueChange={setFilter} className="mb-6">
          <TabsList>
            <TabsTrigger value="awaiting_pickup">{t("delivery.filterAwaitingPickup")}</TabsTrigger>
            <TabsTrigger value="assigned">{t("delivery.filterMyJobs")}</TabsTrigger>
            <TabsTrigger value="picked_up">{t("delivery.filterInDelivery")}</TabsTrigger>
            <TabsTrigger value="delivered">{t("delivery.filterDelivered")}</TabsTrigger>
            <TabsTrigger value="all">{t("delivery.filterAll")}</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-omu-blue-500" /></div>
        ) : filteredJobs.length === 0 ? (
          <Card className="border-0 shadow-soft">
            <CardContent className="p-10 text-center text-slate-500">
              <Package className="h-10 w-10 mx-auto mb-2 opacity-40" />
              {t("delivery.emptyList")}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredJobs.map((job) => {
              const st = STATUS[job.status] || STATUS.awaiting_pickup;
              const mine = job.assigned_to === user?.id;
              return (
                <Card key={job.id} className="border-0 shadow-soft">
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{job.pharmacy_name}</p>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" /> {job.pharmacy_address || t("delivery.pharmacyAddressMissing")}
                        </p>
                      </div>
                      <Badge className={st.className}>{st.label}</Badge>
                    </div>
                    <div className="text-sm bg-slate-50 rounded-xl p-3">
                      <p><strong>{t("delivery.client")} :</strong> {job.client_name || "—"}</p>
                      {job.client_phone && <p className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {job.client_phone}</p>}
                      {job.client_address && <p className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {job.client_address}</p>}
                      <p className="mt-1">
                        {(job.items || []).map((i) => `${i.medicine_name || t("delivery.product")} × ${i.quantity || 1}`).join(", ")}
                      </p>
                      <p className="font-medium text-omu-blue-600 mt-1">
                        {(job.grand_total || ((job.total_price || 0) + (job.delivery_fee || 0))).toLocaleString()} CDF — {t("delivery.paid")}
                      </p>
                      {(job.delivery_fee > 0 || job.delivery_zone_name) && (
                        <p className="text-xs text-slate-500">
                          {t("delivery.productsDeliveryBreakdown", {
                            products: (job.total_price || 0).toLocaleString(),
                            delivery: (job.delivery_fee || 0).toLocaleString(),
                          })}
                          {job.delivery_zone_name ? ` (${job.delivery_zone_name})` : ""}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {job.status === "awaiting_pickup" && (
                        <Button size="sm" className="rounded-full bg-omu-blue-500" disabled={acting === job.id} onClick={() => act(job.id, "take")}>
                          {t("delivery.takeJob")}
                        </Button>
                      )}
                      {mine && job.status === "assigned" && (
                        <Button size="sm" className="rounded-full" disabled={acting === job.id} onClick={() => act(job.id, "pickup")}>
                          <Package className="h-4 w-4 mr-1" /> {t("delivery.markPickedUp")}
                        </Button>
                      )}
                      {mine && (job.status === "picked_up" || job.status === "assigned") && (
                        <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700" disabled={acting === job.id} onClick={() => act(job.id, "deliver")}>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> {t("delivery.markDelivered")}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default DeliveryDashboard;
